import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import {
  Timer,
  AssistantPhoto,
  Today,
  CheckCircle,
  AccountCircle,
  AccountBox,
  NewReleases,
  DateRange,
  PhoneIphone,
  Phone,
} from '@mui/icons-material';

import {
  paths,
  PatientAccountStatus,
  TASK_PRIORITY,
  TASK_RECURRENCE,
} from '@common/types';
import { capitalize } from 'lodash';
import { formatDateShort } from '@util/date.ts';
import slack from '@assets/slack.svg';
import { theme } from '@store';
import { t } from 'i18next';
import { useSaveTaskMutation } from '@common/cache';
import { Select } from 'ui';
import { toast } from 'react-toastify';
import { TruncatedTextWithConditionalTooltip } from '../../TruncatedTableRow';
import { Email } from '@mui/icons-material';
import dayjs from 'dayjs';
import { getDaysPast } from '@util/getDaysPast.ts';
import { getPriorityColor } from '@util/getPriorityColor.ts';
import { OpenInNew } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { getAccountStatusColor } from 'util/user.ts';
import { ClipboardButton } from 'components';
import { statusMapper } from 'util/statusMapper.ts';

interface IPatientInfoProps {
  task: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json'];
}

export const PatientInfo = ({ task }: IPatientInfoProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(245, 245, 245, 1)',
        p: 2,
        border: '1px solid rgba(0, 0, 0, 0.12)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='subtitle2'>Patient information</Typography>
        <IconButton
          component={RouterLink}
          sx={{ p: 0, borderRadius: '4px' }}
          to={`/patient/${task.patient.id}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          <OpenInNew htmlColor={theme.palette.semiBlack.main} />
        </IconButton>
      </Box>

      <TruncatedTextWithConditionalTooltip
        width='450px'
        text={task.patient.name}
        variant='body2'
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 1,
          alignItems: 'center',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Email
              sx={{ height: '16px', width: '16px' }}
              htmlColor={theme.palette.semiBlack.main}
            />
            {task.patient.email ? (
              <>
                <Typography sx={{ wordBreak: 'break-word' }} variant='body2'>
                  {task.patient.email}
                </Typography>
                <ClipboardButton textToCopy={task.patient.email} size={16} />
              </>
            ) : (
              <>-</>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <PhoneIphone
              sx={{ width: '16px', height: '16px' }}
              htmlColor={theme.palette.semiBlack.main}
            />
            {task.patient.phone_number ? (
              <>
                <Typography sx={{ wordBreak: 'break-word' }} variant='body2'>
                  {task.patient.phone_number.replace(
                    /(\d{3})(\d{3})(\d{4})/,
                    '($1) $2-$3',
                  )}
                </Typography>
                <ClipboardButton
                  textToCopy={task.patient.phone_number}
                  size={16}
                />
              </>
            ) : (
              <>-</>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '48px',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            pl: 2,
          }}
        >
          {task.patient.phone_number ? (
            <a
              href={`https://dialpad.com/launch?phone=${task.patient.phone_number.replace(/[^\d+]/g, '')}`}
              // target='_blank'
              rel='noopener noreferrer'
              style={{
                display: 'inline-flex',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <Phone color='primary' />
            </a>
          ) : (
            <Phone color='disabled' />
          )}
        </Box>
      </Box>

      <Divider orientation='horizontal' sx={{ p: 0, marginY: 2 }} />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t(`worklist.worklistItem.overview.accountStatus`)}
        </Typography>

        <Chip
          label={capitalize(task.patient.account_status)}
          sx={{
            backgroundColor: getAccountStatusColor(
              task.patient.account_status as PatientAccountStatus,
            ),
            color: theme =>
              task.patient.account_status === 'Inactive'
                ? 'rgba(0, 0, 0, 0.6)'
                : theme.palette.common.white,
            height: '24px',
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t(`worklist.worklistItem.overview.guideStatus`)}
        </Typography>

        <Typography variant='body2'>
          {task.patient.guide_status || '-'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t(`worklist.worklistItem.overview.tier`)}
        </Typography>

        {task.patient.guide_tier ? (
          <Chip
            label={task.patient.guide_tier}
            sx={{
              height: 24,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            }}
          />
        ) : (
          <>-</>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t(`worklist.worklistItem.overview.guideAlignmentDate`)}
        </Typography>

        <Typography variant='body2'>
          {task.patient.guide_alignment_date
            ? dayjs(task.patient.guide_alignment_date).format('MMM D, YYYY')
            : '-'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t(`worklist.worklistItem.overview.practice`)}
        </Typography>

        <Typography variant='body2'>
          {task.patient?.practice?.name || '-'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t(`worklist.worklistItem.overview.location`)}
        </Typography>

        <Typography variant='body2'>
          {task.patient?.location?.name || 'Unknown'}
        </Typography>
      </Box>

      <Button
        variant='outlined'
        disabled={!task.canvas_link}
        component='a'
        href={task.canvas_link ?? undefined}
        target='_blank'
        rel='noopener noreferrer'
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: 'fit-content',
          textDecoration: 'none',
          textTransform: 'none',
        }}
      >
        {task.canvas_link ? 'VIEW IN EMR' : 'EMR link not found'}
      </Button>
    </Box>
  );
};

interface IProviderInfoProps {
  task: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json'];
}

export const ProviderInfo = ({ task }: IProviderInfoProps) => {
  const { provider } = task;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(245, 245, 245, 1)',
        p: 2,
        border: '1px solid rgba(0, 0, 0, 0.12)',
      }}
    >
      <Typography variant='subtitle2'>
        {t(`worklist.worklistItem.overview.provider`)}
      </Typography>

      <TruncatedTextWithConditionalTooltip
        width='500px'
        text={`${provider.name} ${provider.npi ? '(NPI: ' + provider.npi + ')' : ''}`}
        variant='body2'
      />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t(`worklist.worklistItem.overview.guideCertified`)}
        </Typography>

        <Typography variant='body2'>
          {task.provider.guide_certified
            ? `Yes (${dayjs(task.provider.guide_certification_date).format('MMM DD, YYYY')})`
            : 'No'}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Email
              sx={{ height: '16px', width: '16px' }}
              htmlColor={theme.palette.semiBlack.main}
            />
            {provider.email ? (
              <>
                <Typography sx={{ wordBreak: 'break-word' }} variant='body2'>
                  {provider.email}
                </Typography>
                <ClipboardButton textToCopy={provider.email} size={16} />
              </>
            ) : (
              <>-</>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <PhoneIphone
              sx={{ height: '16px', width: '16px' }}
              htmlColor={theme.palette.semiBlack.main}
            />
            {provider.phone_number ? (
              <>
                <Typography sx={{ wordBreak: 'break-word' }} variant='body2'>
                  {provider.phone_number}
                </Typography>
                <ClipboardButton textToCopy={provider.phone_number} size={16} />
              </>
            ) : (
              <>-</>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <img
              width='16px'
              height='16px'
              src={slack}
              style={{
                objectFit: 'cover',
                alignSelf: 'center',
              }}
              alt='Slack'
            />
            {provider.slack_link ? (
              <Typography
                sx={{
                  wordBreak: 'break-word',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  fontSize: '0.7rem',
                  '& a': {
                    display: 'flex',
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                }}
                variant='body1'
              >
                <Link
                  href={provider.slack_link}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <TruncatedTextWithConditionalTooltip
                    width='175px'
                    text={provider.slack_link}
                    variant='body2'
                  />
                </Link>
              </Typography>
            ) : (
              '-'
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '76px',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            pl: 2,
          }}
        >
          {provider.phone_number ? (
            <a
              href={`https://dialpad.com/launch?phone=${provider.phone_number.replace(/[^\d+]/g, '')}`}
              // target='_blank'
              rel='noopener noreferrer'
              style={{
                display: 'inline-flex',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <Phone color='primary' />
            </a>
          ) : (
            <Phone color='disabled' />
          )}
        </Box>
      </Box>
    </Box>
  );
};

interface ITaskInfo {
  task: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json'];
  setIsSavingTask: (isSaving: boolean) => void;
  isDisabled?: boolean;
}

export const TaskInfo = ({
  task,
  setIsSavingTask,
  isDisabled = false,
}: ITaskInfo) => {
  const saveTaskMutation = useSaveTaskMutation(task.id);

  const recurrenceOptions = Object.entries(TASK_RECURRENCE)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      value: value.toString(),
      label: t(`worklist.recurrence.${key.toLowerCase()}`),
    }));

  const handleSaveTask = async (recurrence: TASK_RECURRENCE) => {
    if (recurrence) {
      try {
        setIsSavingTask(true);
        await saveTaskMutation.mutateAsync({
          ...task,
          program: task.program,
          recurrence: recurrence,
        });
      } catch {
        toast.error('Failed to save changes.');
      } finally {
        setIsSavingTask(false);
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      {task.order_category && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.orderCategory`)}
          </Typography>
          <Typography variant='body2'>{task.order_category}</Typography>
        </Box>
      )}

      {task.order_type && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.orderType`)}
          </Typography>
          <Typography variant='body2'>{task.order_type}</Typography>
        </Box>
      )}

      {task.order_set && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.orderSet`)}
          </Typography>
          <Typography variant='body2'>{task.order_set}</Typography>
        </Box>
      )}

      {task.lab_requirements && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.requirements`)}
          </Typography>
          <TruncatedTextWithConditionalTooltip
            width='320px'
            text={task.lab_requirements}
            variant='body2'
          />
        </Box>
      )}

      {task.home_health_agency_name && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.agencyName`)}
          </Typography>
          <TruncatedTextWithConditionalTooltip
            width='320px'
            text={task.home_health_agency_name}
            variant='body2'
          />
        </Box>
      )}

      {task.home_health_provider_status && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.statusOfHome`)}
          </Typography>
          <Typography variant='body2'>
            {task.home_health_provider_status}
          </Typography>
        </Box>
      )}

      {task.hospice_referral_origin && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.hospiceReferral`)}
          </Typography>

          <TruncatedTextWithConditionalTooltip
            width='320px'
            text={task.hospice_referral_origin}
            variant='body2'
          />
        </Box>
      )}

      {task.external_hospice_agency_name && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.externalHospice`)}
          </Typography>
          <TruncatedTextWithConditionalTooltip
            width='284px'
            text={task.external_hospice_agency_name}
            variant='body2'
          />
        </Box>
      )}

      {task.external_hospice_referral_reason && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.externalReason`)}
          </Typography>
          <TruncatedTextWithConditionalTooltip
            width='320px'
            text={task.external_hospice_referral_reason}
            variant='body2'
          />
        </Box>
      )}

      {task.external_agency_name && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.externalAgencyName`)}
          </Typography>
          <TruncatedTextWithConditionalTooltip
            width='275px'
            text={task.external_agency_name}
            variant='body2'
          />
        </Box>
      )}

      {task.reason_for_external_referral && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.reasonForExternalReferral`)}
          </Typography>
          <TruncatedTextWithConditionalTooltip
            width='275px'
            text={task.reason_for_external_referral}
            variant='body2'
          />
        </Box>
      )}

      {task.other_specifier && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.palette.semiBlack.main }}
          >
            {t(`worklist.worklistItem.overview.otherSpecifier`)}
          </Typography>
          <TruncatedTextWithConditionalTooltip
            width='275px'
            text={task.other_specifier}
            variant='body2'
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <Timer htmlColor={theme.palette.semiBlack.main} />
        <Typography
          variant='body2'
          sx={{ color: theme.palette.semiBlack.main }}
        >
          {t(`worklist.worklistItem.overview.minutes`)}
        </Typography>
        <Typography variant='body2'>{task.expected_minutes || '-'}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <NewReleases htmlColor={theme.palette.semiBlack.main} />
        <Typography
          variant='body2'
          sx={{ color: theme.palette.semiBlack.main }}
        >
          {t(`worklist.worklistItem.overview.urgency`)}
        </Typography>
        <Typography variant='body2'>{task.urgency || '-'}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <AssistantPhoto htmlColor={theme.palette.semiBlack.main} />
        <Typography
          variant='body2'
          sx={{ color: theme.palette.semiBlack.main }}
        >
          {t(`worklist.worklistItem.overview.priority`)}
        </Typography>
        <Chip
          label={capitalize(task.priority)}
          sx={{
            height: 24,
            backgroundColor: getPriorityColor(task.priority as TASK_PRIORITY),
            color: theme => theme.palette.common.white,
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <Today htmlColor={theme.palette.semiBlack.main} />
        <Typography
          variant='body2'
          sx={{ color: theme.palette.semiBlack.main }}
        >
          {t(`worklist.worklistItem.overview.dueDate`)}
        </Typography>
        <Typography
          variant='body2'
          sx={{ color: task.priority === 'Overdue' ? 'error' : 'unset' }}
        >
          {formatDateShort(task.due_date)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <CheckCircle htmlColor={theme.palette.semiBlack.main} />
        <Typography
          variant='body2'
          sx={{ color: theme.palette.semiBlack.main }}
        >
          {t(`worklist.worklistItem.overview.status`)}
        </Typography>
        <Typography variant='body2'>
          {statusMapper({
            status: task.status,
            canvas_status: task.canvas_status,
          })}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <DateRange htmlColor={theme.palette.semiBlack.main} />
        <Typography
          variant='body2'
          sx={{ color: theme.palette.semiBlack.main }}
        >
          {t(`worklist.worklistItem.overview.createdDate`)}
        </Typography>
        <Typography variant='body2'>
          {dayjs(task.created_date).format('MMM DD, YYYY')}
          {task.completion_date ? '' : ` (${getDaysPast(task.created_date)})`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <AccountCircle htmlColor={theme.palette.semiBlack.main} />
        <Typography
          variant='body2'
          sx={{ color: theme.palette.semiBlack.main }}
        >
          {t(`worklist.worklistItem.overview.assignee`)}
        </Typography>
        <AccountBox />
        {task.assignment.type === 'Team' ? (
          <Typography variant='body2'>{task.assignment.name}</Typography>
        ) : (
          <>
            <Typography variant='body2'>{task.assignment.name}</Typography>
            <Typography variant='body2'>
              ({task.assignment.team_name})
            </Typography>
          </>
        )}
      </Box>
      {(task.custom_task || task.category === 'Care management') && (
        <Select
          sx={{ mt: '1rem' }}
          name='recurrence'
          label={t('worklist.recurrence.label')}
          disabled={isDisabled}
          options={recurrenceOptions}
          clearable={false}
          value={task.recurrence?.toString() as unknown as string}
          onChange={event => {
            const val = event.target.value;
            if (val) {
              handleSaveTask(val as unknown as TASK_RECURRENCE);
            }
          }}
          {...(task.recurrence && {
            helperText:
              task.recurrence === -1
                ? t('worklist.recurrence.infoOneTime')
                : t('worklist.recurrence.infoRecurrent', {
                    days: task.recurrence,
                  }),
          })}
        />
      )}
    </Box>
  );
};
